import React, { useState, useMemo } from "react";
import { MenuItem, Tooltip } from "@material-ui/core";
import Menu from "@material-ui/core/Menu";
import { DeviceModel } from "../../../../Models/devices";
import ViewModule from "@material-ui/icons/ViewModule";
import LensOutlinedIcon from "@material-ui/icons/LensOutlined";
import BorderAllOutlinedIcon from "@material-ui/icons/BorderAllOutlined";
import ViewStream from "@material-ui/icons/ViewStream";
import { FISHEYE_VIEW_TYPES } from "../../../../../Pages/Cameras/components/PlaybackWebRTC/FishEyeGridSettings";
import { useTranslation } from "react-i18next";
import { MultiLayerMenuOptionData } from "../../../../../Pages/Cameras/components/PlaybackWebRTC/MultiLayerMenu";
import EPtzIcon from "../../../Icons/EPtzIcon";

interface Props {
  buttonStyles: string;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  device?: DeviceModel;
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  changeDewarperView: (viewType: FISHEYE_VIEW_TYPES) => void;
}

const FishEyePlayerButton = ({ buttonStyles, device, containerRef, changeDewarperView }: Props) => {
  const fishEyeModelSet = new Set(["VA-DFE12A-1TB"]);
  const isFishEye = () => device && fishEyeModelSet.has(device.model);
  const PARENT_CONTAINER = containerRef.current;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClose = () => setAnchorEl(null);
  const { t } = useTranslation();
  const fishEyeOptions = [
    {
      name: "Single",
      getIcon: () => <LensOutlinedIcon />,
      shouldBeDisabled: () => false,
      shouldDisplay: () => true,
      onClick: () => changeDewarperView(FISHEYE_VIEW_TYPES.SINGLE),
    },
    {
      name: "ePTZ",
      getIcon: () => <EPtzIcon />,
      shouldBeDisabled: () => false,
      shouldDisplay: () => true,
      onClick: () => changeDewarperView(FISHEYE_VIEW_TYPES.E_PTZ),
    },
    {
      name: "Quad",
      getIcon: () => <BorderAllOutlinedIcon />,
      shouldBeDisabled: () => false,
      shouldDisplay: () => true,
      onClick: () => changeDewarperView(FISHEYE_VIEW_TYPES.QUAD),
    },
    {
      name: "Perimeter",
      getIcon: () => <ViewStream />,
      shouldBeDisabled: () => false,
      shouldDisplay: () => true,
      onClick: () => changeDewarperView(FISHEYE_VIEW_TYPES.PERIMETER),
    },
  ];

  if (!isFishEye()) {
    return null;
  }

  const handleToggleButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  };

  const onOptionWasClicked = (action: MultiLayerMenuOptionData) => {
    if (action.onClick) {
      action.onClick();
    }
    setAnchorEl(null);
  };

  const menuItems = useMemo(
    () =>
      fishEyeOptions.map((action: MultiLayerMenuOptionData) => (
        <MenuItem onClick={() => onOptionWasClicked(action)} key={action.name}>
          <Tooltip
            title={action.tooltipText ? action.tooltipText : `${t(action.name)}`}
            placement="left"
            key={action.name}
          >
            <span>{action.getIcon()}</span>
          </Tooltip>
        </MenuItem>
      )),
    []
  );

  return (
    <>
      <button
        id="fisheye-options-toggle-button"
        className={buttonStyles}
        type="button"
        onClick={handleToggleButtonClick}
      >
        <ViewModule />
      </button>
      <Menu
        id="fisheye-options-events-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        container={PARENT_CONTAINER}
      >
        {menuItems}
      </Menu>
    </>
  );
};

export default FishEyePlayerButton;
